/*
 * bundle-builder.js — "Build Your Bundle" picker cart wiring
 * -----------------------------------------------------------------------
 * Deliberately NOT reusing bag-selector.js's cart mechanics (separate
 * cart-add call + remove/re-add reorder dance). This page owns its own
 * add-to-cart button end-to-end, so it does one combined /cart/add.js call
 * with both line items instead. See /plan-eng-review Architecture Issue 1.
 *
 * Non-atomicity: Shopify's /cart/add.js does not document all-or-nothing
 * behavior for multi-item requests (community-reported partial adds despite
 * a success-looking response). After the call returns, this verifies both
 * variant IDs are actually present in /cart.js before treating it as a real
 * success. See Cross-Model Tension 1.
 *
 * Cart-update event contract in this theme is inconsistent across actions
 * (bag-selector.js documents theme:cart:refresh for adds; bag-cart-guard.js
 * documents theme:cart:added + rebuy:cart.change for removals). Dispatches
 * multiple event names defensively rather than assuming one works
 * everywhere. See Architecture Issue 7.
 */
(function () {
  var root = document.querySelector('[data-section-type="bundle-builder"]');
  if (!root) return;

  var atcBtn = root.querySelector('[data-bundle-builder-atc]');
  var atcLabel = root.querySelector('[data-bundle-builder-atc-label]');
  var summaryLine = root.querySelector('[data-bundle-builder-summary]');
  var priceStrike = root.querySelector('[data-bundle-builder-price-strike]');
  var priceFinal = root.querySelector('[data-bundle-builder-price-final]');
  var savingsBadge = root.querySelector('[data-bundle-builder-savings]');
  var variantHint = root.querySelector('[data-bundle-builder-variant-hint]');
  var errorBox = root.querySelector('[data-bundle-builder-error]');
  var handpanList = root.querySelector('[data-bundle-builder-list="handpan"]');
  var bundleList = root.querySelector('[data-bundle-builder-list="bundle"]');
  if (!atcBtn) return;

  var CART_REFRESH_EVENTS = ['theme:cart:refresh', 'theme:cart:added', 'theme:cart:change', 'cart:updated'];

  var state = { handpan: null, bundle: null, working: false };

  function selectedCard(list) {
    if (!list) return null;
    var input = list.querySelector('[data-bundle-picker-input]:checked');
    if (!input) return null;
    var card = input.closest('[data-bundle-picker-card]');
    if (!card) return null;
    var result = {
      variantId: parseInt(card.getAttribute('data-variant-id'), 10),
      price: parseInt(card.getAttribute('data-price'), 10),
      compareAt: parseInt(card.getAttribute('data-compare-at'), 10) || 0,
      // Set by the variant selector (bundle-picker-variant-selector.liquid /
      // initVariantSelector) when the product has variants but the shopper
      // hasn't chosen one yet — e.g. the workbook's language. Treated as
      // "not ready": disables the summary/Add-to-cart until resolved.
      pending: card.hasAttribute('data-variant-pending'),
      bagVariantId: null,
      bagPriceDelta: 0,
      freeBagVariantId: null
    };
    // The bag dropdown (if this handpan has one) lives inside the same
    // .bundle-picker-card-wrap as the card's <label> — a sibling of the
    // label, never nested inside it (see bundle-picker-card.liquid for why:
    // label + nested interactive control would fight over clicks). "card"
    // here is the wrapper (data-bundle-picker-card lives there now), so the
    // dropdown is a descendant, not a sibling, of "card".
    var bagDropdown = card.querySelector('[data-bundle-bag-dropdown]');
    if (bagDropdown) {
      // data-free-bag-variant-id is EMPTY when the free bag is sold out (see
      // bundle-picker-bag-dropdown.liquid) — never fall back to a variant
      // /cart/add.js would refuse.
      var freeAttr = bagDropdown.getAttribute('data-free-bag-variant-id');
      result.freeBagVariantId = freeAttr ? parseInt(freeAttr, 10) : null;
      var hidden = bagDropdown.querySelector('[data-bundle-bag-value]');
      if (hidden && hidden.value) {
        result.bagVariantId = parseInt(hidden.value, 10);
        var selectedOpt = bagDropdown.querySelector('[data-bundle-bag-type][data-variant="' + hidden.value + '"]');
        if (selectedOpt) {
          result.bagPriceDelta = parseInt(selectedOpt.getAttribute('data-price-delta'), 10) || 0;
        }
      } else if (result.freeBagVariantId) {
        result.bagVariantId = result.freeBagVariantId;
      }
      // This handpan needs a bag but none is purchasable (all sold out):
      // block the add-to-cart instead of letting /cart/add.js 422.
      result.bagUnavailable = bagDropdown.hasAttribute('data-bag-none-available') || !result.bagVariantId;
      result.bagNoneMsg = bagDropdown.getAttribute('data-msg-bags-none') || 'All bags are currently sold out.';
    }
    return result;
  }

  function moneyFromCents(cents) {
    // window.theme.moneyFormat (== shop.money_format) is tied to the shop's
    // BASE currency and does NOT update when a customer switches currency
    // via Shopify Markets (confirmed: this store has a real country/currency
    // switcher — snippets/cross-border.liquid — and window.Shopify.currency
    // is genuinely populated at runtime, already used elsewhere in theme.js).
    // Liquid's variant.price is already converted to the visitor's active
    // currency by the time it reaches this page (Markets converts at the
    // object level, not just at display time), so the raw cents here are
    // already correct — only the SYMBOL/FORMAT needs to match the active
    // currency, which Shopify.currency.active + Intl.NumberFormat handles
    // correctly for any currency without depending on a stored format string.
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: window.Shopify.currency.active });
    }
    // Fallback: no Shopify.currency means Markets multi-currency isn't active
    // on this store, so the base format is definitely correct.
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      var format = (window.theme.settings && window.theme.settings.currency_code_enable)
        ? window.theme.moneyWithCurrencyFormat
        : window.theme.moneyFormat;
      return window.theme.formatMoney(cents, format);
    }
    return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'EUR' });
  }

  function clearError() {
    errorBox.textContent = '';
    errorBox.classList.remove('is-visible');
  }

  function showError(message) {
    errorBox.textContent = message;
    errorBox.classList.add('is-visible');
  }

  function setLoading(on) {
    state.working = on;
    atcBtn.classList.toggle('is-loading', on);
    atcLabel.textContent = on ? 'Adding...' : 'Add to cart';
    updateSummary();
  }

  function updateSummary() {
    state.handpan = selectedCard(handpanList);
    state.bundle = selectedCard(bundleList);

    var handpanReady = state.handpan && !state.handpan.pending;
    var bundleReady = state.bundle && !state.bundle.pending;
    var bagBlocked = !!(state.handpan && state.handpan.bagUnavailable);
    if (bagBlocked && handpanReady) {
      showError(state.handpan.bagNoneMsg);
    }

    if (handpanReady && bundleReady && !bagBlocked) {
      var bagDelta = state.handpan.bagPriceDelta || 0;
      var total = state.handpan.price + state.bundle.price + bagDelta;
      // Combined strikethrough: BOTH discounts count — the handpan's own
      // compare-at (e.g. 1500 -> 1400) plus the bundle option's.
      var compareTotal =
        (state.handpan.compareAt || state.handpan.price) +
        (state.bundle.compareAt || state.bundle.price) +
        bagDelta;
      summaryLine.textContent = '';
      priceStrike.textContent = compareTotal > total ? moneyFromCents(compareTotal) : '';
      priceFinal.textContent = moneyFromCents(total);
      if (savingsBadge) {
        var saved = compareTotal - total;
        if (saved > 0) {
          savingsBadge.textContent = (savingsBadge.getAttribute('data-savings-prefix') || 'You save') + ' ' + moneyFromCents(saved);
          savingsBadge.hidden = false;
        } else {
          savingsBadge.hidden = true;
        }
      }
      atcBtn.disabled = state.working;
      atcBtn.style.opacity = state.working ? '0.7' : '1';
      if (variantHint) variantHint.hidden = true;
    } else {
      priceStrike.textContent = '';
      priceFinal.textContent = '-';
      if (savingsBadge) savingsBadge.hidden = true;
      atcBtn.disabled = true;
      atcBtn.style.opacity = '0.35';
      // Both cards are picked but a variant choice (e.g. the workbook's
      // language) is still open — that's the ONLY case where the disabled
      // button needs explaining right here; with a card still unselected,
      // the summary prompt already says what to do.
      if (variantHint) {
        var bothSelected = !!(state.handpan && state.bundle);
        var anyPending = bothSelected && (state.handpan.pending || state.bundle.pending);
        variantHint.hidden = !anyPending;
      }
    }
  }

  function syncSelectedClass(list) {
    if (!list) return;
    var cards = list.querySelectorAll('[data-bundle-picker-card]');
    cards.forEach(function (card) {
      var input = card.querySelector('[data-bundle-picker-input]');
      card.classList.toggle('is-selected', !!(input && input.checked));
    });
  }

  function onSelectionChange() {
    syncSelectedClass(handpanList);
    syncSelectedClass(bundleList);
    clearError();
    updateSummary();
    fitAllBullets();
  }

  if (handpanList) handpanList.addEventListener('change', onSelectionChange);
  if (bundleList) bundleList.addEventListener('change', onSelectionChange);

  // Description text should use exactly as much vertical space as the photo
  // takes up — no more (truncate with "+N more"), no less (show every
  // bullet that fits). The photo's height is only known after layout (it's
  // responsive, driven by the card's rendered width via aspect-ratio), so
  // this has to run client-side, not in Liquid.
  function fitBullets(card) {
    // Respect a manual "+N more" expand (see toggleBulletsExpand) — a
    // resize recompute shouldn't silently re-collapse something the
    // customer explicitly opened.
    if (card.classList.contains('is-bullets-expanded')) return;
    var media = card.querySelector('[data-bundle-picker-media]');
    var body = card.querySelector('.bundle-picker-card__body');
    var bulletsList = card.querySelector('[data-bundle-picker-bullets]');
    var moreLine = card.querySelector('[data-bullets-more]');
    if (!media || !body || !bulletsList || !moreLine) return;

    var bulletItems = Array.prototype.slice.call(bulletsList.querySelectorAll('[data-bullet]'));
    bulletItems.forEach(function (li) { li.hidden = false; });
    moreLine.hidden = true;

    var availableHeight = media.getBoundingClientRect().height;
    if (!availableHeight || body.scrollHeight <= availableHeight) return;

    var hiddenCount = 0;
    for (var i = bulletItems.length - 1; i >= 0; i--) {
      bulletItems[i].hidden = true;
      hiddenCount++;
      moreLine.hidden = false;
      moreLine.textContent = '+' + hiddenCount + ' more';
      if (body.scrollHeight <= availableHeight) break;
    }
  }

  function fitAllBullets() {
    var cards = root.querySelectorAll('[data-bundle-picker-card]');
    Array.prototype.forEach.call(cards, fitBullets);
  }

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(fitAllBullets, 150);
  });

  // Mobile card lists scroll horizontally (see @media rule in
  // bundle-builder.liquid). The scroll-hint arrow fades out once the last
  // card is fully in view, so it only signals "there's more" while that's
  // actually true.
  function initScrollHints() {
    if (typeof IntersectionObserver === 'undefined') return;
    var wraps = root.querySelectorAll('[data-bundle-builder-list-wrap]');
    Array.prototype.forEach.call(wraps, function (wrap) {
      var list = wrap.querySelector('[data-bundle-builder-list]');
      if (!list) return;
      var cards = list.querySelectorAll('[data-bundle-picker-card]');
      var lastCard = cards[cards.length - 1];
      if (!lastCard) return;
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          wrap.classList.toggle('is-scroll-end', entry.isIntersecting);
        });
      }, { root: list, threshold: 0.98 });
      observer.observe(lastCard);
    });
  }

  // "+N more" is clickable — expands to show every bullet, click again
  // (or the equivalent Enter/Space, since it's a role="button" <li>, not
  // a real <button>) to re-collapse back to the fitted amount.
  function toggleBulletsExpand(card) {
    var expanded = card.classList.toggle('is-bullets-expanded');
    var moreLine = card.querySelector('[data-bullets-more]');
    if (expanded) {
      var bulletsList = card.querySelector('[data-bundle-picker-bullets]');
      if (bulletsList) {
        Array.prototype.forEach.call(bulletsList.querySelectorAll('[data-bullet]'), function (li) {
          li.hidden = false;
        });
      }
      if (moreLine) moreLine.textContent = 'Show less';
    } else {
      fitBullets(card);
    }
  }

  function initBulletsExpand() {
    root.addEventListener('click', function (e) {
      var moreLine = e.target.closest('[data-bullets-more]');
      if (!moreLine) return;
      var card = moreLine.closest('[data-bundle-picker-card]');
      if (card) toggleBulletsExpand(card);
    });
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var moreLine = e.target.closest('[data-bullets-more]');
      if (!moreLine) return;
      e.preventDefault();
      var card = moreLine.closest('[data-bundle-picker-card]');
      if (card) toggleBulletsExpand(card);
    });
  }

  // Bag dropdown — adapted from bag-selector.js's UI-interaction code (open/
  // close, choose type, colour swatches) but with NO cart-add logic at all.
  // Selection is read directly by selectedCard() above; this only maintains
  // the dropdown's own visual state and the hidden data-bundle-bag-value
  // input, then dispatches a bubbling 'change' event so the existing
  // handpanList 'change' listener (onSelectionChange) picks it up — the
  // dropdown is a DOM descendant of handpanList (sibling of the card it
  // belongs to), so this requires no extra wiring.
  function initBagDropdown(ddRoot) {
    var dd = ddRoot.querySelector('[data-bundle-bag-panel-root]');
    if (!dd) return;
    var toggle = dd.querySelector('[data-bundle-bag-toggle]');
    var panel = dd.querySelector('[data-bundle-bag-panel]');
    var curName = dd.querySelector('[data-bundle-bag-current]');
    var curPrice = dd.querySelector('[data-bundle-bag-current-price]');
    var colorsWrap = dd.querySelector('[data-bundle-bag-colors]');
    var hidden = dd.querySelector('[data-bundle-bag-value]');
    var rows = Array.prototype.slice.call(dd.querySelectorAll('[data-bundle-bag-type]'));
    var rowsEnabled = rows.filter(function (r) { return r.getAttribute('aria-disabled') !== 'true'; });
    var activeEl = null;
    var selectedName = '';
    // See .bundle-builder__list.has-open-dropdown in bundle-builder.liquid —
    // the mobile horizontal-scroll list clips this panel unless we
    // temporarily lift its overflow while the panel is open.
    var scrollList = ddRoot.closest('.bundle-builder__list');

    function setValue(id) {
      if (!id) return;
      hidden.value = id;
      ddRoot.dispatchEvent(new CustomEvent('change', { bubbles: true }));
    }

    function isOpen() { return dd.classList.contains('is-open'); }
    function open() {
      dd.classList.add('is-open'); panel.hidden = false; toggle.setAttribute('aria-expanded', 'true');
      if (scrollList) scrollList.classList.add('has-open-dropdown');
      setActive(panel.querySelector('.is-selected') || rowsEnabled[0]);
      setTimeout(function () { document.addEventListener('click', onOutside, true); }, 0);
    }
    function close() {
      dd.classList.remove('is-open'); panel.hidden = true; toggle.setAttribute('aria-expanded', 'false');
      if (scrollList) scrollList.classList.remove('has-open-dropdown');
      document.removeEventListener('click', onOutside, true);
    }
    function onOutside(e) { if (!dd.contains(e.target)) close(); }
    function setActive(el) {
      if (!el) return;
      var prev = panel.querySelector('.is-active'); if (prev) prev.classList.remove('is-active');
      el.classList.add('is-active'); activeEl = el; el.scrollIntoView({ block: 'nearest' });
    }

    function showColoursFor(handle) {
      if (!colorsWrap) return null;
      var match = null;
      Array.prototype.forEach.call(colorsWrap.querySelectorAll('[data-bundle-colors-for]'), function (s) {
        var on = s.getAttribute('data-bundle-colors-for') === handle;
        s.hidden = !on; if (on) match = s;
      });
      colorsWrap.hidden = !match;
      return match;
    }
    function updateToggle(colourName) {
      curName.textContent = selectedName + (colourName ? ' \u00b7 ' + colourName : '');
    }

    function chooseType(row) {
      if (!row || row.getAttribute('aria-disabled') === 'true') return;
      var prev = panel.querySelector('.is-selected');
      if (prev) { prev.classList.remove('is-selected'); prev.setAttribute('aria-selected', 'false'); }
      row.classList.add('is-selected'); row.setAttribute('aria-selected', 'true');

      selectedName = row.getAttribute('data-name') || '';
      var isFree = row.getAttribute('data-kind') === 'free';
      curPrice.textContent = row.getAttribute('data-price-label') || '';
      curPrice.classList.toggle('is-free', isFree);
      setValue(row.getAttribute('data-variant'));

      var set = showColoursFor(row.getAttribute('data-handle'));
      var colourName = '';
      if (set) {
        var swatches = set.querySelectorAll('.bundle-bag-swatch');
        Array.prototype.forEach.call(swatches, function (s) { s.classList.remove('is-on'); });
        var firstAvail = set.querySelector('.bundle-bag-swatch:not([data-unavailable])') || swatches[0];
        if (firstAvail) {
          firstAvail.classList.add('is-on');
          setValue(firstAvail.getAttribute('data-variant'));
          colourName = firstAvail.getAttribute('data-name') || '';
        }
      }
      updateToggle(colourName);
    }

    if (colorsWrap) {
      colorsWrap.addEventListener('click', function (e) {
        var sw = e.target.closest('.bundle-bag-swatch');
        if (!sw || sw.hasAttribute('data-unavailable')) return;
        var set = sw.closest('[data-bundle-colors-for]');
        Array.prototype.forEach.call(set.querySelectorAll('.bundle-bag-swatch'), function (s) { s.classList.remove('is-on'); });
        sw.classList.add('is-on');
        setValue(sw.getAttribute('data-variant'));
        updateToggle(sw.getAttribute('data-name') || '');
        showSwatchZoomPreview(sw);
      });
    }

    toggle.addEventListener('click', function (e) { e.preventDefault(); isOpen() ? close() : open(); });
    panel.addEventListener('click', function (e) {
      var li = e.target.closest('[data-bundle-bag-type]'); if (li) { chooseType(li); close(); toggle.focus(); }
    });
    panel.addEventListener('mousemove', function (e) {
      var li = e.target.closest('[data-bundle-bag-type]');
      if (li && li.getAttribute('aria-disabled') !== 'true') setActive(li);
    });
    dd.addEventListener('keydown', function (e) {
      var k = e.key;
      if (!isOpen()) {
        if (k === 'Enter' || k === ' ' || k === 'ArrowDown' || k === 'ArrowUp') { e.preventDefault(); open(); }
        return;
      }
      if (k === 'Escape' || k === 'Tab') { close(); if (k === 'Escape') { e.preventDefault(); toggle.focus(); } return; }
      if (k === 'Enter' || k === ' ') { e.preventDefault(); chooseType(activeEl); close(); toggle.focus(); return; }
      if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Home' || k === 'End') {
        e.preventDefault();
        var idx = rowsEnabled.indexOf(activeEl);
        if (k === 'Home') idx = 0;
        else if (k === 'End') idx = rowsEnabled.length - 1;
        else if (k === 'ArrowDown') idx = Math.min(rowsEnabled.length - 1, idx + 1);
        else idx = Math.max(0, idx - 1);
        setActive(rowsEnabled[idx]);
      }
    });

    // Initialize from the pre-selected row (free, per the Liquid template).
    chooseType(panel.querySelector('.bundle-bag-dropdown__opt.is-selected') || rowsEnabled[0]);
  }

  function initBagDropdowns() {
    var dropdowns = root.querySelectorAll('[data-bundle-bag-dropdown]');
    Array.prototype.forEach.call(dropdowns, initBagDropdown);
  }

  // Standalone variant picker for handpan/bundle-option cards — fully
  // separate from the bag-dropdown functions above (no shared state, no
  // shared classes). Updates the card wrapper's own data-variant-id/
  // data-price/data-compare-at attributes directly, the same attributes
  // selectedCard() (below) already reads off every card, then dispatches
  // 'change' so the existing summary/cart logic picks it up unmodified.
  //
  // No variant is pre-selected (merchant wants a conscious choice — e.g. the
  // workbook's language DE/EN — not a silent default). Until EVERY option
  // group has a value chosen, the card is "pending": data-variant-pending is
  // set on the wrapper, which selectedCard()/updateSummary() treat as
  // incomplete (Add-to-cart stays disabled), and the "Please choose" hint
  // (.is-pending on the selector) is shown.
  function initVariantSelector(el) {
    var dataScript = el.querySelector('[data-bundle-variant-data]');
    if (!dataScript) return;
    var variants;
    try { variants = JSON.parse(dataScript.textContent); } catch (e) { return; }
    var cardWrap = el.closest('[data-bundle-picker-card]');
    if (!cardWrap) return;
    var optionRows = Array.prototype.slice.call(el.querySelectorAll('.bundle-variant-selector__option'));

    function currentSelection() {
      return optionRows.map(function (row) {
        var active = row.querySelector('.bundle-variant-pill.is-selected');
        return active ? active.getAttribute('data-value') : null;
      });
    }

    function allChosen(selection) {
      return selection.every(function (val) { return val != null; });
    }

    // Matches with null treated as a wildcard — used for greying out pills
    // that can't lead to any (available) variant, before a full selection.
    function findVariant(selection) {
      var matches = variants.filter(function (v) {
        return selection.every(function (val, i) { return val == null || v.options[i] === val; });
      });
      return matches[0] || null;
    }

    function setPending(isPending) {
      el.classList.toggle('is-pending', isPending);
      if (isPending) {
        cardWrap.setAttribute('data-variant-pending', 'true');
      } else {
        cardWrap.removeAttribute('data-variant-pending');
      }
    }

    function applyVariant(variant) {
      if (!variant) return;
      cardWrap.setAttribute('data-variant-id', variant.id);
      cardWrap.setAttribute('data-price', variant.price);
      cardWrap.setAttribute('data-compare-at', variant.compareAtPrice || 0);
    }

    function refreshAvailability() {
      var selection = currentSelection();
      Array.prototype.forEach.call(el.querySelectorAll('.bundle-variant-pill'), function (pill) {
        var idx = parseInt(pill.getAttribute('data-option-index'), 10);
        var testSelection = selection.slice();
        testSelection[idx] = pill.getAttribute('data-value');
        var match = findVariant(testSelection);
        pill.classList.toggle('is-unavailable', !match || !match.available);
      });
    }

    el.addEventListener('click', function (e) {
      var pill = e.target.closest('.bundle-variant-pill');
      if (!pill || pill.classList.contains('is-unavailable')) return;
      var row = pill.closest('.bundle-variant-selector__option');
      Array.prototype.forEach.call(row.querySelectorAll('.bundle-variant-pill'), function (p) { p.classList.remove('is-selected'); });
      pill.classList.add('is-selected');
      refreshAvailability();

      var selection = currentSelection();
      if (allChosen(selection)) {
        applyVariant(findVariant(selection));
        setPending(false);
      } else {
        setPending(true);
      }
      // Re-run summary/cart state either way (resolved -> price shows &
      // Add-to-cart may enable; still-pending -> stays disabled).
      cardWrap.dispatchEvent(new CustomEvent('change', { bubbles: true }));
    });

    // Start pending: nothing is chosen yet, and a choice is required.
    setPending(true);
    refreshAvailability();
  }

  function initVariantSelectors() {
    var selectors = root.querySelectorAll('[data-bundle-variant-selector]');
    Array.prototype.forEach.call(selectors, initVariantSelector);
  }

  // Advertorial reason blocks can offer two product videos (e.g. the 440 Hz
  // and 432 Hz demos) with a pill toggle — clicking a pill swaps the shared
  // iframe's src. Markup comes from bundle-advertorial-blocks.liquid.
  function initVideoSwitchers() {
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-bb-video-btn]');
      if (!btn || btn.classList.contains('is-selected')) return;
      var media = btn.closest('.bb-reason__media');
      if (!media) return;
      var frame = media.querySelector('[data-bb-video-frame]');
      if (!frame) return;
      frame.src = btn.getAttribute('data-video-src');
      Array.prototype.forEach.call(media.querySelectorAll('[data-bb-video-btn]'), function (b) {
        b.classList.toggle('is-selected', b === btn);
      });
    });
  }

  // Review carousel in advertorial reason blocks: one slide visible at a
  // time, arrows cycle with wrap-around, counter shows "n / total".
  function initReviewCarousels() {
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-bb-rev-prev], [data-bb-rev-next]');
      if (!btn) return;
      var car = btn.closest('[data-bb-rev-carousel]');
      if (!car) return;
      var slides = car.querySelectorAll('[data-bb-rev-slide]');
      if (!slides.length) return;
      var idx = parseInt(car.getAttribute('data-rev-index') || '0', 10);
      idx += btn.hasAttribute('data-bb-rev-next') ? 1 : -1;
      idx = (idx + slides.length) % slides.length;
      car.setAttribute('data-rev-index', String(idx));
      Array.prototype.forEach.call(slides, function (s, i) {
        s.classList.toggle('is-active', i === idx);
      });
      // Counter lives just outside the carousel element (sibling line).
      var body = car.parentNode;
      var counter = body ? body.querySelector('[data-bb-rev-count]') : null;
      if (counter) counter.textContent = (idx + 1) + ' / ' + slides.length;
    });
  }

  // Briefly shows the enlarged picture floating over a tapped swatch, then
  // fades it out — touch equivalent of the PDP's desktop-only hover zoom.
  // Triggered directly from the swatch tap handler below, not a separate
  // control.
  var swatchZoomTimer = null;
  function showSwatchZoomPreview(sw) {
    var wrap = sw.closest('.bundle-bag-swatch-wrap');
    if (!wrap || !wrap.querySelector('[data-bundle-swatch-zoom-preview]')) return;
    Array.prototype.forEach.call(root.querySelectorAll('.bundle-bag-swatch-wrap.is-zoomed'), function (w) {
      w.classList.remove('is-zoomed');
    });
    wrap.classList.add('is-zoomed');
    clearTimeout(swatchZoomTimer);
    swatchZoomTimer = setTimeout(function () { wrap.classList.remove('is-zoomed'); }, 1500);
  }

  function addToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = new Error(data.description || data.message || 'Could not add to cart.');
          err.cartResponse = data;
          throw err;
        }
        return data;
      });
    });
  }

  function verifyCart(expectedVariantIds) {
    return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      var presentIds = cart.items.map(function (i) { return i.variant_id; });
      var missing = expectedVariantIds.filter(function (id) { return presentIds.indexOf(id) === -1; });
      if (missing.length > 0) {
        var err = new Error('One of the selected items could not be added. Please try again.');
        err.missing = missing;
        throw err;
      }
      return cart;
    });
  }

  function dispatchCartRefresh() {
    CART_REFRESH_EVENTS.forEach(function (name) {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
    });
  }

  atcBtn.addEventListener('click', function () {
    if (state.working || atcBtn.disabled) return;
    updateSummary();
    if (!state.handpan || !state.bundle) return;
    // A card with a variant selector but no chosen value (e.g. workbook
    // language not picked) is not ready — do not add a silent default.
    if (state.handpan.pending || state.bundle.pending) return;

    // Defense in depth: updateSummary already disables the button in this
    // state, but never build a cart call around a bag that cannot be added.
    if (state.handpan.bagUnavailable) {
      showError(state.handpan.bagNoneMsg);
      return;
    }

    clearError();
    setLoading(true);

    // Site-wide rule (bag-cart-guard.js): every handpan must always carry a
    // bag. Add whichever bag the customer actually chose in the dropdown
    // (free by default, or their upgrade). _default_bag names the FREE bag's
    // variant (the guard's fallback-if-removed target) and is only set while
    // that variant is actually purchasable — when the free bag is sold out
    // the attribute is empty and the line stays untagged, exactly like
    // bag-selector.js, so bag-cart-guard never retries an impossible add.
    var bundleId = 'bb-' + Date.now();
    var handpanItem = { id: state.handpan.variantId, quantity: 1 };
    var items = [handpanItem, { id: state.bundle.variantId, quantity: 1 }];
    var expectedIds = [state.handpan.variantId, state.bundle.variantId];

    if (state.handpan.bagVariantId) {
      handpanItem.properties = { _bundle: bundleId };
      if (state.handpan.freeBagVariantId) {
        handpanItem.properties._default_bag = String(state.handpan.freeBagVariantId);
      }
      items.push({ id: state.handpan.bagVariantId, quantity: 1, properties: { _bundle: bundleId } });
      expectedIds.push(state.handpan.bagVariantId);
    }

    addToCart(items)
      .then(function () { return verifyCart(expectedIds); })
      .then(function () {
        setLoading(false);
        dispatchCartRefresh();
      })
      .catch(function (err) {
        setLoading(false);
        showError(err.message || 'Something went wrong. Please try again.');
      });
  });

  function initValueStacks() {
    // Value-stack rows marked data-bb-value-base carry typed shop-base-
    // currency (EUR) amounts: Liquid's `| money` swaps the symbol for the
    // active currency but never converts the amount. Convert those rows
    // with the base->presentment rate Shopify exposes on every page, then
    // recompute the total and save-% so all lines stay consistent. Rows
    // without the marker were resolved from a live product price and are
    // already in the visitor's currency.
    var rate = 1;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
      rate = parseFloat(window.Shopify.currency.rate) || 1;
    }
    Array.prototype.forEach.call(root.querySelectorAll('.bb-value'), function (stack) {
      // Only touch the stack when something actually needs converting;
      // then re-render EVERY row through the same formatter, so Liquid-
      // formatted and JS-converted amounts don't mix two number formats
      // (e.g. "$174.00" next to "45,32 $") inside one box.
      if (rate === 1 || !stack.querySelector('[data-bb-value-base]')) return;
      var total = 0;
      Array.prototype.forEach.call(stack.querySelectorAll('[data-bb-value-cents]'), function (row) {
        var cents = parseInt(row.getAttribute('data-bb-value-cents'), 10) || 0;
        if (row.hasAttribute('data-bb-value-base')) cents = Math.round(cents * rate);
        row.textContent = moneyFromCents(cents);
        total += cents;
      });
      var totalEl = stack.querySelector('[data-bb-value-total]');
      if (totalEl) totalEl.textContent = moneyFromCents(total);
      var finalEl = stack.querySelector('[data-bb-value-final]');
      if (finalEl) {
        var bundleCents = parseInt(finalEl.getAttribute('data-bb-bundle-cents'), 10) || 0;
        var pct = total > 0 && bundleCents > 0 ? Math.round(((total - bundleCents) / total) * 100) : 0;
        finalEl.textContent = moneyFromCents(bundleCents) + (pct > 0 ? ' · save ' + pct + '%' : '');
      }
    });
  }

  initBagDropdowns();
  initVariantSelectors();
  initBulletsExpand();
  initScrollHints();
  initVideoSwitchers();
  initReviewCarousels();
  initValueStacks();
  updateSummary();
  fitAllBullets();
})();
